import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Headphones, Package, BookOpen, Play, Music, FileText } from "lucide-react";
import { AudioPlayer } from "@/components/hm/AudioPlayer";
import { useAuth } from "@/context/AuthContext";
import { Dawn, Arrow } from "@/components/hm/Basics";
import { LandingNav } from "@/screens/landing/Top";
import { SiteFooter } from "@/components/hm/SiteFooter";
import { api, EMPTY_TEXT } from "@/lib/api";
import { mediaUrl } from "@/admin/ui";
import { RESOURCE_LANDINGS } from "@/screens/resources/landings";

const ICONS = { "meditacion-corazon-helicoidal": Headphones, "pack-iniciacion": Package, "resumen-la-energia-del-corazon": BookOpen };

export default function ResourceLanding() {
  const { slug } = useParams();
  const { user } = useAuth();
  const r = RESOURCE_LANDINGS.find((x) => x.slug === slug);
  const [slots, setSlots] = useState({});
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    setSlots({});
    setHidden(false);
    if (r) api.get(`/resources/${slug}`).then((res) => { setSlots(res.data.slots); setHidden(!res.data.visible); }).catch(() => {});
  }, [slug, r]);
  if (!r || hidden) return <Navigate to="/" replace />;
  const Icon = ICONS[r.slug];
  const hasContent = Object.values(slots).some((l) => l.length);
  const locked = !user;
  const src = (f) => (f && !locked ? mediaUrl(f.url) : "");
  const media = "pdf" in r.media ? { pdf: src(slots.pdf?.[0]) }
    : r.media.collections ? { collections: r.media.collections.map((c) => ({ ...c, count: slots[c.id]?.length || 0,
      items: locked ? [] : (slots[c.id] || []).map((f) => ({ title: f.name.replace(/\.[^.]+$/, ""), src: src(f) })) })) }
      : { video: src(slots.video?.[0]), audio: src(slots.audio?.[0]) };
  const ready = { pdf: !!slots.pdf?.length, video: !!slots.video?.length, audio: !!slots.audio?.length };

  return (
    <div className={r.dark ? "welcome-mod force-dark" : undefined} style={r.dark ? { marginBottom: 0 } : undefined}>
      <LandingNav />
      <main style={{ position: "relative", isolation: "isolate", overflow: "hidden", paddingTop: 140, paddingBottom: 90, minHeight: "80svh" }} data-testid={`resource-landing-${r.slug}`}>
        <Dawn style={{ opacity: r.dark ? 0.25 : 0.35 }} />
        <div className="wrap" style={{ maxWidth: 760, position: "relative" }}>
          <span className="card-ico" style={{ marginBottom: "1.4rem" }}><Icon /></span>
          <span className="eyebrow">{r.eyebrow} · Recurso gratuito</span>
          <h1 className="hm-h1" style={{ margin: "1.1rem 0 0" }} data-testid="resource-landing-title">{r.pageTitle || r.title}</h1>
          <p className={r.details ? "und-sub" : "lead"} style={{ marginTop: "1.2rem", textAlign: "left" }} data-testid="resource-landing-text">{r.subtitle || r.short}</p>
          {r.details && <ResourceDetails d={r.details} />}
          <div className={`wm-read ${r.dark ? "res-block" : ""}`} style={{ marginTop: "2rem" }} data-testid="resource-landing-block">
            {r.lockedText && locked && r.lockedText.map((t, i) => <p key={t} style={i ? undefined : { marginTop: 0 }} data-testid={i ? undefined : "resource-landing-locked"}>{t}</p>)}
            {!hasContent && !(r.lockedText && locked) && <>
              <p style={{ marginTop: 0 }} data-testid="resource-landing-empty">{EMPTY_TEXT}</p>
              <p>Crea tu cuenta gratis. Así tendrás acceso en cuanto esté disponible. Tu proceso es privado y solo tú verás lo que escribas.</p>
            </>}
            {hasContent && locked && !r.lockedText && <p style={{ marginTop: 0 }} data-testid="resource-landing-locked">Ya está disponible. Crea tu cuenta gratis o entra para acceder. Tu proceso es privado y solo tú verás lo que escribas.</p>}
            {(!r.minimal || (!locked && hasContent)) && <MediaSlots media={media} title={r.pageTitle || r.title} ready={ready} hidePlaceholders={r.minimal} />}
          </div>
          <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginTop: "2rem" }}>
            {user ? (
              <Link to="/inicio" className={`btn btn-primary wm-btn ${r.dark ? "btn-gold-hover" : ""}`} data-testid="resource-landing-go-app">Ir a mi espacio <Arrow /></Link>
            ) : (
              <>
                <Link to="/registro" className={`btn btn-primary wm-btn ${r.dark ? "btn-gold-hover" : ""}`} data-testid="resource-landing-register">Crear mi cuenta gratis <Arrow /></Link>
                {!r.minimal && <Link to="/entrar" className={`btn btn-ghost ${r.dark ? "btn-gold-hover" : ""}`} data-testid="resource-landing-login">Entrar</Link>}
              </>
            )}
            <Link to="/#respira" className={`btn btn-ghost ${r.dark ? "btn-gold-hover" : ""}`} data-testid="resource-landing-back">Volver a Recursos</Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

const ResourceDetails = ({ d }) => (
  <div className="res-details" data-testid="resource-landing-description">
    {d.intro.map((t) => <p key={t}>{t}</p>)}
    {d.bullets && <ul>{d.bullets.map((t) => <li key={t}>{t}</li>)}</ul>}
    {d.outro && [].concat(d.outro).map((t) => <p key={t}>{t}</p>)}
  </div>
);

const WAIT = (ready) => (ready ? "Disponible al crear tu cuenta" : "Llegará pronto");

const Collections = ({ list }) => (
  <div className="pack-cols">
    {list.map((c) => (
      <section key={c.id} className="pack-col" data-testid={`resource-collection-${c.id}`}>
        <div className="pack-col-h">
          <span className="audio-play" aria-hidden="true"><Music /></span>
          <div><h4>{c.title}</h4><small>{c.count} de {c.total} · {c.items.length ? "Disponibles" : c.count ? "Disponibles al crear tu cuenta" : "Llegará pronto"}</small></div>
        </div>
        {c.items.length > 0 && (
          <div className="pack-items">{c.items.map((it) => <AudioPlayer key={it.src} src={it.src} title={it.title} />)}</div>
        )}
      </section>
    ))}
  </div>
);

const PdfSlot = ({ src, title, ready }) => (
  <div className="pack-col pdf-slot" data-testid={src ? "resource-pdf" : "resource-pdf-placeholder"}>
    <div className="pack-col-h">
      <span className="audio-play" aria-hidden="true" style={src ? { opacity: 1 } : undefined}><FileText /></span>
      <div><h4>Ebook en PDF</h4><small>{src ? title : WAIT(ready)}</small></div>
      {src && <a href={src} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm btn-gold-hover" style={{ marginLeft: "auto" }} data-testid="resource-pdf-download">Descargar</a>}
    </div>
  </div>
);

const MediaSlots = ({ media, title, ready, hidePlaceholders }) => ("pdf" in media ? <div className="pack-cols"><PdfSlot src={media.pdf} title={title} ready={ready.pdf} /></div> : media.collections ? <Collections list={media.collections} /> : (
  <div className="media-slots">
    {media.video ? (
      <video className="media-video" src={media.video} controls preload="metadata" data-testid="resource-video" />
    ) : !hidePlaceholders && (
      <div className="media-video media-ph" data-testid="resource-video-placeholder">
        <span className="audio-play" aria-hidden="true"><Play /></span>
        <small>Vídeo introductorio · 1–2 min · {WAIT(ready.video)}</small>
      </div>
    )}
    {media.audio ? (
      <AudioPlayer src={media.audio} title={title} />
    ) : !hidePlaceholders && (
      <div className="audio-card is-empty media-audio-ph" data-testid="resource-audio-placeholder">
        <button className="audio-play" disabled aria-label="Audio no disponible"><Headphones /></button>
        <p>Audio de la meditación · {WAIT(ready.audio)}</p>
      </div>
    )}
  </div>
));
